import { useState, useEffect, useRef } from 'react';
import { aboutSteps } from '../data/portfolioData';

const BLOB_SHAPES = [
  'M 80 0 C 80 44 44 80 0 80 C -44 80 -80 44 -80 0 C -80 -44 -44 -80 0 -80 C 44 -80 80 -44 80 0 Z',
  'M 90 -10 C 70 50 30 90 -20 70 C -80 50 -90 -10 -60 -60 C -20 -90 60 -70 90 -10 Z',
  'M 60 -40 C 100 20 60 80 10 70 C -80 90 -90 0 -50 -60 C -10 -100 40 -80 60 -40 Z',
  'M 70 -30 C 90 30 50 100 -10 80 C -100 70 -70 -40 -40 -80 C 10 -90 50 -70 70 -30 Z',
];

export const About = () => {
  const [step, setStep] = useState(0);
  const [animating, setAnimating] = useState(false);
  const sectionRef = useRef(null);
  const prevStep = useRef(-1);

  useEffect(() => {
    const STEP_H = () => window.innerHeight * 0.8; // 80vh per step

    // Step i activates when the i-th step element's midpoint crosses the viewport center.
    // Step elements start after the 100vh sticky panel.
    // Formula: active = floor((scrolledIntoSection - 0.5*vh) / 0.8*vh), clamped to [0, n-1]
    const onScroll = () => {
      const section = sectionRef.current;
      if (!section) return;

      const scrolledIn = -section.getBoundingClientRect().top;
      const sh = STEP_H();
      const mid = window.innerHeight * 0.5;

      const active = Math.max(
        0,
        Math.min(
          aboutSteps.length - 1,
          Math.floor((scrolledIn - mid) / sh),
        ),
      );

      if (active !== prevStep.current) {
        prevStep.current = active;
        setAnimating(true);
        setTimeout(() => {
          setStep(active);
          setAnimating(false);
        }, 160);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const current = aboutSteps[step] || aboutSteps[0];

  return (
    <section id="about" className="scrolly" ref={sectionRef}>
      {/* position:sticky — must NOT have overflow or transform on any ancestor */}
      <div className="scrolly-sticky">
        <div className="scrolly-stage">
          <svg className="blob" viewBox="-100 -100 200 200" aria-hidden="true">
            <defs>
              <linearGradient id="blobG" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%"   stopColor="var(--c-pink)" />
                <stop offset="50%"  stopColor="var(--c-lilac)" />
                <stop offset="100%" stopColor="var(--c-mint)" />
              </linearGradient>
            </defs>
            <path
              d={BLOB_SHAPES[step % BLOB_SHAPES.length]}
              fill="url(#blobG)"
              style={{ transition: 'd .6s ease' }}
            />
          </svg>

          <div className="scrolly-copy">
            <div className="scrolly-num">
              ABOUT — <span>{current.num}</span>/0{aboutSteps.length}
            </div>
            <h2
              className="scrolly-title"
              style={{
                opacity: animating ? 0 : 1,
                transform: animating ? 'translateY(14px)' : 'none',
                transition: 'opacity .18s, transform .18s',
              }}
            >
              {current.title}
            </h2>
            <p
              className="scrolly-body"
              style={{
                opacity: animating ? 0 : 1,
                transform: animating ? 'translateY(14px)' : 'none',
                transition: 'opacity .18s, transform .18s',
              }}
            >
              {current.body}
            </p>

            {/* Step indicator dots */}
            <div className="scrolly-dots" aria-hidden="true">
              {aboutSteps.map((_, i) => (
                <span key={i} className={`sdot ${step === i ? 'active' : ''}`} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll-height padding — gives the section enough height to scroll through */}
      <div className="scrolly-steps">
        {aboutSteps.map((_, i) => (
          <div key={i} className="step" data-step={i} />
        ))}
      </div>
    </section>
  );
};
